import React from "react";
import { Clock, Lock, Minus, Plus } from "lucide-react";
import { Switch } from "../ui/switch";
import { euro, workLabel } from "../../lib/format";
import { fmtQty, priceSuffix, qtyWithUnit } from "../../lib/units";

export default function ProposalServiceItems({ changeQty, cur, locked, quote, selections, toggleOptional }) {
  return (
    <>
      <h2 className="text-lg font-bold text-slate-900 mb-2">Servizi inclusi</h2>
      {quote.items.map((item) => {
        const sel = selections[item.id] || {};
        const enabled = item.item_type === "optional" ? sel.enabled !== false : true;
        const baseQty = item.item_type === "variable" ? sel.quantity ?? item.quantity : item.quantity;
        const qty = baseQty;
        const changedQty = item.item_type === "variable" && Number(qty) !== Number(item.quantity);
        const gross = enabled ? (item.unit_price || 0) * qty : 0;
        let disc = 0;
        if (enabled && item.discount_type === "percent")
          disc = (gross * Math.min(Math.max(item.discount_value || 0, 0), 100)) / 100;
        else if (enabled && item.discount_type === "amount")
          disc = Math.min(Math.max(item.discount_value || 0, 0), gross);
        const lineTotal = Math.round((gross - disc + Number.EPSILON) * 100) / 100;
        return (
          <div
            key={item.id}
            className={`rounded-xl border p-5 transition-all duration-200 ${
              item.item_type === "optional" && !enabled
                ? "border-slate-200 bg-slate-50/50 opacity-70"
                : "border-slate-200 bg-card shadow-sm"
            }`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-slate-900">{item.title}</h3>
                  {item.item_type === "base" && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                      <Lock style={{ width: 11, height: 11 }} /> Incluso
                    </span>
                  )}
                  {item.item_type === "optional" && (
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600">Opzionale</span>
                  )}
                </div>
                {item.description && <p className="text-sm text-slate-500 mt-1 leading-relaxed">{item.description}</p>}
                <div className="flex items-center gap-3 mt-3 text-sm">
                  <span className="font-mono text-slate-700">{euro(item.unit_price, cur)}<span className="text-slate-400"> {priceSuffix(item.unit)}</span></span>
                  <span data-testid={`public-item-qty-${item.id}`} className={`text-xs font-semibold px-2 py-0.5 rounded-full ${changedQty ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>
                    Q.tà {qtyWithUnit(qty, item.unit)}{changedQty ? ` (era ${fmtQty(item.quantity)})` : ""}
                  </span>
                  {disc > 0 && (
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                      Sconto {item.discount_type === "percent" ? `${item.discount_value}%` : euro(item.discount_value, cur)}
                    </span>
                  )}
                  {item.work_days > 0 && (
                    <span className="inline-flex items-center gap-1 text-slate-400">
                      <Clock style={{ width: 13, height: 13 }} /> Tempistica stimata: {workLabel(item.work_days, item.work_unit)}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex flex-col items-end gap-3 shrink-0">
                {item.item_type === "optional" && (
                  <Switch
                    data-testid={`public-proposal-addon-toggle-${item.id}`}
                    checked={enabled}
                    disabled={locked}
                    onCheckedChange={(v) => toggleOptional(item.id, v)}
                  />
                )}
                {item.item_type === "variable" && (
                  <div className="flex items-center gap-2">
                    <button
                      data-testid={`public-proposal-quantity-minus-${item.id}`}
                      onClick={() => changeQty(item, -1)}
                      disabled={locked || qty <= (item.min_qty || 1)}
                      className="w-8 h-8 rounded-full border border-slate-300 flex items-center justify-center text-slate-600 hover:border-primary hover:text-primary disabled:opacity-40 transition-colors"
                    >
                      <Minus style={{ width: 15, height: 15 }} />
                    </button>
                    <span className="font-mono font-semibold w-8 text-center">{fmtQty(qty)}</span>
                    <button
                      data-testid={`public-proposal-quantity-plus-${item.id}`}
                      onClick={() => changeQty(item, 1)}
                      disabled={locked || qty >= (item.max_qty || 999)}
                      className="w-8 h-8 rounded-full border border-slate-300 flex items-center justify-center text-slate-600 hover:border-primary hover:text-primary disabled:opacity-40 transition-colors"
                    >
                      <Plus style={{ width: 15, height: 15 }} />
                    </button>
                  </div>
                )}
                <div className="text-right">
                  {disc > 0 && (
                    <span className="block font-mono text-xs text-slate-400 line-through">{euro(gross, cur)}</span>
                  )}
                  <span className="font-mono font-bold text-slate-900">{euro(lineTotal, cur)}</span>
                </div>
              </div>
            </div>
          </div>
        );
      })}

    </>
  );
}
